import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import React, { useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import { useMergeValue } from '../../hooks/useMergeValue.js';
import { TabsContext } from './context.js';
import defaultStyles from './styles/Tabs.module.scss.js';
import { TabContent } from './TabContent.js';
import { TabHeader } from './TabHeader.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { omit } from '../../utils/omit.js';
import { isFunction } from '../../utils/is.js';

const getPaneChildren = props => {
  const {
    children
  } = props;
  const panelChildren = [];
  React.Children.forEach(children, child => {
    if (child && child.type && child.type.isTabPanel) {
      panelChildren.push(child);
    }
  });
  return panelChildren;
};
const Tabs = /*#__PURE__*/React.forwardRef((props, ref) => {
  const panelChildren = getPaneChildren(props);
  const tabsRef = useRef();
  const [activeTab, setActiveTab] = useMergeValue(panelChildren[0] && panelChildren[0].key, {
    defaultValue: 'defaultActiveTab' in props ? props.defaultActiveTab : undefined,
    value: 'activeTab' in props ? props.activeTab : undefined
  });
  const size = props.size;
  const {
    tabPosition = 'top',
    theme,
    direction,
    lazyload = true,
    type,
    onChange,
    onClickTab,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const tabPos = direction === 'vertical' ? 'left' : tabPosition;
  const tabHeaderProps = {
    activeTab,
    tabPosition,
    direction: ['left', 'right'].indexOf(tabPos) > -1 ? 'vertical' : 'horizontal',
    panelChildren,
    onClickTab: key => {
      isFunction(onClickTab) && onClickTab(key);
      if (key !== activeTab) {
        if (!('activeTab' in props)) {
          setActiveTab(key);
        }
        isFunction(onChange) && onChange(key);
      }
    }
  };
  const TabContentDom = /*#__PURE__*/jsx(TabContent, {
    activeTab: activeTab,
    panelChildren: panelChildren,
    lazyload: lazyload,
    type: type
  });
  return /*#__PURE__*/jsx("div", {
    ...omit(rest, ['headerPadding', 'tabPosition', 'defaultActiveTab', 'extra', 'onAddTab', 'activeTab', 'overflow', 'renderTabTitle', 'icons', 'children', 'size', 'type', 'offsetAlign']),
    className: styles.root,
    ref: mergeRefs([tabsRef, ref]),
    children: /*#__PURE__*/jsxs(TabsContext.Provider, {
      value: tabHeaderProps,
      children: [/*#__PURE__*/jsx(TabHeader, {
        ...omit(props, ['children', 'style', 'className']),
        size: size
      }), TabContentDom]
    })
  });
});
Tabs.displayName = 'Tabs';

export { Tabs };
